import React from "react";

import { PathParamsWithId } from "commons/types/api/common";
import { OptionsType, ResponseType } from "commons/types/http";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import { blockEnumMap } from "utils/blockEnumMap";

export type ButtonDeleteInterface = (
    pathParams: PathParamsWithId,
    options?: OptionsType,
    headers?: {
        [x: string]: string;
    }
) => ResponseType<null>;

type PropsType = {
    buttonDelete: ButtonDeleteInterface;
};

export type TitleFunctionTopBlockHookType = {
    buttonCloseBlock: () => void;
    onButtonDeleteClick: () => void;
    closedBlock: boolean;
    title: string;
};

function useTitleFunctionsTopBlock({ buttonDelete }: PropsType) {
    const { openedBlock } = useBlockGroupContext();

    const [closedBlock, setClosedBlock] = React.useState<boolean>(openedBlock);

    const buttonCloseBlock = React.useCallback(() => {
        setClosedBlock((prev) => !prev);
    }, []);

    const { addSaveAction, removeSaveAction } = useSaveActionsContext();

    const { blockGroupCompositionId, blockId, removeBlock } = useBlockGroupContext();

    const title: string = React.useMemo(() => blockEnumMap[blockId].title, [blockId]);

    const onButtonDeleteClick = React.useCallback(() => {
        removeBlock(blockGroupCompositionId);

        removeSaveAction(`${blockGroupCompositionId}-update-graphical-order`);

        addSaveAction(blockGroupCompositionId, () => {
            const [apiCall] = buttonDelete({ id: blockGroupCompositionId });

            apiCall.then(() => removeSaveAction(blockGroupCompositionId));

            return apiCall;
        });
    }, [removeBlock, buttonDelete, removeSaveAction, addSaveAction, blockGroupCompositionId]);

    return React.useMemo(
        (): TitleFunctionTopBlockHookType => ({
            buttonCloseBlock,
            onButtonDeleteClick,
            closedBlock,
            title,
        }),
        [buttonCloseBlock, onButtonDeleteClick, closedBlock, title]
    );
}

export default useTitleFunctionsTopBlock;
